import { useState } from "react"
import { Pencil, Trash2 } from "lucide-react"
import { Channel } from "../../../../../types"
import { useDeleteChannelMutation } from "../../../../../redux/queries"
import { useAppDispatch } from "../../../../hooks"
import { pushToast } from "../../../../../redux/slices/notifications"
import Modal from "../../../modal"
import { PrimaryBtn, LightBtn } from "../../../buttons"

type Props = {
  info: Channel
  onSelect: (val: string) => void
}

const ActionsCell = ({ info, onSelect }: Props) => {
  const { id, name } = info
  const dispatch = useAppDispatch()
  const [deleteChannel, { isLoading: isDeleting }] = useDeleteChannelMutation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect(id)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteChannel(id).unwrap()
      dispatch(
        pushToast({
          msg: "Channel deleted successfully",
          theme: "success"
        })
      )
      setShowDeleteModal(false)
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to delete channel",
          theme: "danger"
        })
      )
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={handleEdit}
        className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
        title="Edit channel"
      >
        <Pencil size={18} />
      </button>
      <button
        onClick={handleDeleteClick}
        className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
        title="Delete channel"
      >
        <Trash2 size={18} />
      </button>

      <Modal
        title="Delete Channel"
        show={showDeleteModal}
        onClose={handleCancelDelete}
      >
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
              <Trash2 size={24} className="text-rose-600" />
            </div>
            <div>
              <p className="text-gray-900 font-medium">Delete "{name}"?</p>
              <p className="text-sm text-gray-500 mt-1">
                This action cannot be undone. All associated data will be lost.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <LightBtn onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </LightBtn>
            <PrimaryBtn
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {isDeleting ? "Deleting..." : "Delete Channel"}
            </PrimaryBtn>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ActionsCell
